import * as Phaser from 'phaser';

/**
 * Менеджер пулов объектов для Phaser игры
 * Управляет пулами снарядов, врагов и других объектов для оптимизации производительности
 */
export class PoolManager {
  private scene: Phaser.Scene | null = null;
  private pools: Map<string, Phaser.GameObjects.Group> = new Map();

  /**
   * Инициализирует менеджер пулов с привязкой к сцене
   * @param scene - сцена Phaser
   */
  public init(scene?: Phaser.Scene): void {
    if (scene) {
      this.scene = scene;
    }
  }

  public getScene(): Phaser.Scene | null {
    return this.scene;
  }

  /**
   * Создает или получает снаряд из пула
   * @param info Объект с информацией о создаваемом снаряде
   * @returns Phaser.Physics.Arcade.Sprite - созданный снаряд
   */
  public spawnProjectile(info: { x: number; y: number; type?: string; }): any {
    if (!this.scene) {
      console.warn('PoolManager не инициализирован сценой');
      return null;
    }

    const type = info.type || 'bullet';
    return this.spawn(info.x, info.y, type);
  }

  /**
   * Создает или получает объект из пула (общий метод)
   * @param x Координата X
   * @param y Координата Y
   * @param type Тип объекта
   * @returns Phaser.GameObjects.GameObject - созданный объект
   */
  public spawn(x: number, y: number, type: string, textureKey: string = type): any {
    if (!this.scene) return null;

    let pool = this.pools.get(type);

    if (!pool) {
      pool = this.scene.physics.add.group({
        classType: Phaser.Physics.Arcade.Sprite,
        runChildUpdate: true
      });
      this.pools.set(type, pool);
    }

    let entity = pool.get(x, y, textureKey);

    if (entity) {
      this.resetVisual(entity);
      entity.setActive(true);
      entity.setVisible(true);
      if (entity.body) {
        (entity.body as Phaser.Physics.Arcade.Body).enable = true;
        (entity.body as Phaser.Physics.Arcade.Body).reset(x, y);
      }
      if (textureKey !== type && typeof entity.setTexture === 'function') {
        entity.setTexture(textureKey);
      }
      if (typeof entity.setData === 'function') {
        entity.setData('poolType', type);
        entity.setData('visualTextureKey', textureKey);
      }
    }

    return entity;
  }

  /**
   * Возвращает объект в пул (деактивирует его)
   * @param entity Объект для возврата в пул
   */
  public despawn(entity: any): void {
    if (!entity) return;

    this.resetVisual(entity);

    entity.setActive(false);
    entity.setVisible(false);

    if (entity.body) {
      (entity.body as Phaser.Physics.Arcade.Body).stop();
      (entity.body as Phaser.Physics.Arcade.Body).enable = false;
    }
  }

  /**
   * Предварительно заполняет пул объектами указанного типа
   * @param type Тип объекта
   * @param count Количество объектов для предварительного создания
   */
  public prefill(type: string, count: number): void {
    if (!this.scene) return;

    let pool = this.pools.get(type);
    if (!pool) {
      pool = this.scene.add.group({
        classType: Phaser.Physics.Arcade.Sprite,
        runChildUpdate: true
      });
      this.pools.set(type, pool);
    }

    pool.createMultiple({
      key: type,
      repeat: count - 1,
      active: false,
      visible: false
    });
  }

  /**
   * Registers a pool with specific configuration
   * @param type Pool key
   * @param config Group configuration
   */
  public registerPool(type: string, config: Phaser.Types.GameObjects.Group.GroupConfig): void {
    if (!this.scene) return;
    if (this.pools.has(type)) return;

    const pool = this.scene.add.group({
      runChildUpdate: true,
      ...config
    });
    this.pools.set(type, pool);
  }

  public resetVisual(entity: any): void {
    entity.getData?.('visualCleanup')?.();
    entity.getData?.('characterVisual')?.reset();
    this.scene?.tweens?.killTweensOf(entity);
    entity.setAlpha?.(1);
    entity.setRotation?.(0);
    entity.setData?.('visualGeneration', Number(entity.getData?.('visualGeneration') ?? 0) + 1);
  }

  /**
   * Registers a physics-capable pool for damage-capable runtime objects.
   * This avoids accidental fallback to display-only groups.
   */
  public registerPhysicsPool(type: string, config: Phaser.Types.GameObjects.Group.GroupConfig = {}): void {
    if (!this.scene) return;
    if (this.pools.has(type)) return;

    const pool = this.scene.physics.add.group({
      classType: Phaser.Physics.Arcade.Sprite,
      runChildUpdate: true,
      ...config
    });
    this.pools.set(type, pool);
  }

  /**
   * Возвращает группу Phaser для указанного типа объектов
   * @param type Тип объекта
   */
  public getGroup(type: string): Phaser.GameObjects.Group | undefined {
    let pool = this.pools.get(type);

    // Если пула нет, но есть сцена, создаем его (default to Sprite)
    if (!pool && this.scene) {
      pool = this.scene.physics.add.group({
        classType: Phaser.Physics.Arcade.Sprite,
        runChildUpdate: true
      });
      this.pools.set(type, pool);
    }

    return pool;
  }

  public getPoolSnapshot(): Record<string, { active: number; total: number; free: number }> {
    const snapshot: Record<string, { active: number; total: number; free: number }> = {};

    this.pools.forEach((group, type) => {
      const children = typeof group.getChildren === 'function' ? group.getChildren() : [];
      const total = children.length;
      const active = children.filter(child => Boolean((child as { active?: boolean }).active)).length;
      snapshot[type] = {
        active,
        total,
        free: Math.max(0, total - active)
      };
    });

    return snapshot;
  }
}

export default PoolManager;
